import { useEffect, useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Plus } from "lucide-react";
import LanguageContext from "./contexts/languageContext"; // Context import 수정

const Header = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [prevScrollY, setPrevScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Context 사용을 위한 useContext 호출
  const context = useContext(LanguageContext);

  // Context가 undefined인 경우를 처리
  if (!context) {
    throw new Error("Header must be used within a LanguageProvider");
  }

  // Context에서 필요한 값들을 추출
  const { language, toggleLanguage } = context;

  // 네비게이션 아이템 정의
  const navItems = {
    en: [
      { name: "Artwork", path: "/artwork" },
      { name: "Biography", path: "/biography" },
      { name: "Contacts", path: "/contacts" },
    ],
    ko: [
      { name: "작품", path: "/artwork" },
      { name: "약력", path: "/biography" },
      { name: "연락처", path: "/contacts" },
    ],
  };

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY === 0) {
        setIsVisible(true);
      } else {
        if (prevScrollY === 0) {
          setIsVisible(false);
        }
      }
      setPrevScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollY]);

  // 모바일 메뉴 오픈 시 스크롤 방지
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMobileMenuOpen]);

  // 현재 언어에 따른 네비게이션 아이템 선택
  const currentNavItems = navItems[language];

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 right-0 z-50 p-7 bg-transparent
          transition-transform duration-300 ease-in-out
          ${isVisible ? "translate-y-0" : "-translate-y-full"}
        `}
      >
        <nav className="mx-auto">
          <div className="flex justify-between items-center">
            <Link to="/" className="no-underline">
              <div
                className="text-3xl font-bold text-gray-800 cursor-pointer"
                style={{
                  fontFamily: '"forma-djr-display", sans-serif',
                  fontWeight: 800,
                  fontStyle: "normal",
                }}
              >
                LEE SUN HEE
              </div>
            </Link>

            <div className="hidden md:flex items-center space-x-12">
              {/* Navigation Items */}
              <ul className="flex space-x-5">
                {currentNavItems.map((item) => (
                  <li key={item.name} className="relative">
                    <Link
                      to={item.path}
                      className="text-gray-800 hover:text-gray-600 cursor-pointer no-underline relative"
                      style={{
                        fontFamily: '"neue-haas-grotesk-display", sans-serif',
                        fontWeight: 700,
                        fontSize: 17,
                        fontStyle: "normal",
                      }}
                    >
                      <span className="relative">
                        {item.name}
                        {location.pathname === item.path && (
                          <span className="absolute bottom-0 left-0 w-full h-px bg-black transform -translate-y-0" />
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Language Toggle Button */}
              <button
                onClick={toggleLanguage}
                className="text-gray-800 hover:text-gray-600 font-medium"
                style={{
                  fontFamily: '"neue-haas-grotesk-display", sans-serif',
                  fontWeight: 700,
                  fontSize: 17,
                }}
              >
                {language === "en" ? "KOR" : "ENG"}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2"
              aria-label="Open menu"
            >
              <Plus className="w-6 h-6 text-gray-800" />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`
          fixed inset-0 bg-white z-50 transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <div className="p-7 flex justify-between items-center">
          <Link
            to="/"
            className="no-underline"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2"
            aria-label="Close menu"
          >
            <X className="w-6 h-6 text-gray-800" />
          </button>
        </div>

        <nav className="p-7">
          <ul className="space-y-6">
            {currentNavItems.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  className="text-gray-800 text-5xl font-bold no-underline block"
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{
                    fontFamily: '"neue-haas-grotesk-display", sans-serif',
                    fontWeight: 500,
                    fontStyle: "normal",
                  }}
                >
                  {item.name}
                </Link>
              </li>
            ))}
            <li>
              <button
                onClick={toggleLanguage}
                className="text-gray-800 text-5xl font-bold mt-6"
                style={{
                  fontFamily: '"neue-haas-grotesk-display", sans-serif',
                  fontWeight: 500,
                }}
              >
                {language === "en" ? "KOR" : "ENG"}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Header;
